import { useTranslation } from 'react-i18next'
import type { ScorecardMark } from './types'

/** Two decimals when there are any, as the board shows them. */
export function formatPoints(points: number): string {
  return Number.isInteger(points) ? String(points) : points.toFixed(2)
}

/**
 * Deep green for a flash, pale green for a top, an outline for nothing.
 *
 * Both tops are green on purpose: a top is good news either way, and a flash
 * is the same news, louder. The letter (F or T) carries the difference too, so
 * the two never rely on shade alone.
 */
export const MARK_STYLE: Record<ScorecardMark | 'none', string> = {
  top: 'bg-[#e3f1e5] text-[#22602c] border-[#e3f1e5] dark:bg-[#1f4a26] dark:text-[#cdeccf] dark:border-[#1f4a26]',
  flash: 'bg-[#2f7a3a] text-white border-[#2f7a3a]',
  none: 'border-divider text-foreground/50',
}

/** F for a flash, T for a top: the letters a climber says out loud. */
export function useMarkGlyph(): (mark: ScorecardMark) => string {
  const { t } = useTranslation()
  return (mark) => (mark === 'flash' ? t('scorecard.glyphFlash') : t('scorecard.glyphTop'))
}

/** The small square used in the legend and the toppers list. */
export function MarkSwatch({ mark }: { mark?: ScorecardMark }) {
  const glyph = useMarkGlyph()
  return (
    <span
      className={`inline-grid size-[18px] shrink-0 place-items-center rounded-[5px] border-[1.5px] text-[10px] font-bold leading-none ${MARK_STYLE[mark ?? 'none']}`}
      aria-hidden="true"
    >
      {mark ? glyph(mark) : null}
    </span>
  )
}
